import { useEffect, useRef } from 'react';
import { todayInputValue } from './taskDates';

/**
 * Call me when the reader's calendar day changes.
 *
 * Three triggers, one check. The check is a string compare against
 * `todayInputValue()` and fires the callback only on an actual change, so the
 * triggers are free to overlap and none of them has to be reliable alone.
 *
 * A timeout aimed at the next local midnight is the primary one, because the
 * case this exists for is a dashboard left open on a second monitor -- visible,
 * never focused, never clicked, and `visibilitychange` would never fire for it.
 * It is scheduled off `setHours(24, 0, 0, 0)` on a real Date, so DST evenings
 * and month ends are the platform's problem rather than ours -- the same reason
 * `shiftDateOnly` goes through a Date instead of adding to the string.
 *
 * It is not enough by itself: background tabs have their timers clamped, and a
 * laptop suspended at 23:50 and opened at 09:00 fires it whenever the browser
 * gets round to it. So coming back to the tab re-checks too, which is the same
 * lazy sweep `openSessionFor` does on the server and for the same reason: the
 * read that would have shown you the stale thing is the moment to fix it.
 *
 * `setInterval` was the other option and is the wrong one -- 1440 wakeups a day
 * to answer "no" 1439 times, and it drifts.
 */
export function useDayBoundary(onRoll: (today: string, previous: string) => void): void {
  // Through a ref so the effect can run once for the life of the component
  // rather than tearing down and rescheduling the midnight timer on every
  // render that happens to rebuild the callback.
  const handler = useRef(onRoll);
  handler.current = onRoll;

  useEffect(() => {
    let seen = todayInputValue();
    let timer = 0;

    const check = () => {
      const today = todayInputValue();
      if (today === seen) return;
      const previous = seen;
      seen = today;
      handler.current(today, previous);
    };

    const schedule = () => {
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      timer = window.setTimeout(() => {
        check();
        schedule();
        // A second of slack, so the timer cannot fire a hair early, read the old
        // date and leave the card stale until something else wakes it.
      }, midnight.getTime() - Date.now() + 1000);
    };

    schedule();
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, []);
}
